import { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { supabase } from '../utils/supabaseClient'
import NaturalBackground from '../components/NaturalBackground'

const ForgotPassword = () => {
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [emailValid, setEmailValid] = useState<boolean | null>(null)

  const validateEmail = (email: string) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return re.test(email)
  }

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setEmail(value)
    if (value) {
      setEmailValid(validateEmail(value))
    } else {
      setEmailValid(null)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError(null)
    setSuccess(null)

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      })
      
      if (error) throw error
      
      setSuccess('Password reset instructions have been sent to your email')
    } catch (err: any) {
      setError(err.message || 'An error occurred')
    } finally {
      setLoading(false)
    }
  }

  return (
    <NaturalBackground variant="sky">
      <div className="min-h-screen flex flex-col">
        <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8 py-12">
          <div className="w-full max-w-md">
            {/* Header */}
            <div className="text-center mb-8">
              <div className="w-16 h-16 mx-auto mb-6 bg-sage-100/60 rounded-full flex items-center justify-center border border-sage-200/30">
                <svg className="w-8 h-8 text-sage-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                </svg>
              </div>
              <h1 className="text-sage-600 font-light text-2xl mb-2">Reset Your Password</h1>
              <p className="text-sage-500 font-light text-sm">
                Enter your email and we'll send you instructions to reset your password
              </p>
            </div>

            {success && (
              <div className="p-4 bg-mint-50/80 border border-mint-100/50 rounded-2xl mb-6">
                <p className="text-mint-600 text-sm font-light text-center">{success}</p>
                <div className="mt-4 text-center">
                  <Link
                    to="/login"
                    className="text-mint-500 text-sm font-light hover:text-mint-600 transition-colors underline"
                  >
                    Return to login
                  </Link>
                </div>
              </div>
            )}

            {error && (
              <div className="p-4 bg-peach-50/60 border border-peach-200/50 rounded-2xl mb-6">
                <p className="text-peach-600 text-sm font-light text-center">{error}</p>
              </div>
            )}

            {!success && (
              <form onSubmit={handleSubmit} className="bg-sage-50/80 backdrop-blur-sm rounded-3xl border border-sage-100/50 p-6 sm:p-8 shadow-lg space-y-6">
                <div>
                  <label htmlFor="email" className="block text-sage-600 font-light text-sm mb-2">
                    Email
                  </label>
                  <div className="relative">
                    <input
                      id="email"
                      type="email"
                      placeholder="your.email@example.com"
                      value={email}
                      onChange={handleEmailChange}
                      className={`w-full p-4 rounded-2xl border ${
                        emailValid === true ? 'border-mint-200/50 focus:border-mint-300' : 
                        emailValid === false ? 'border-peach-200/50 focus:border-peach-300' : 
                        'border-sage-200/50 focus:border-sage-300'
                      } focus:ring-2 focus:ring-sage-100/50 focus:outline-none font-light text-sm bg-white/50`}
                      required
                      aria-invalid={emailValid === false}
                      aria-describedby={emailValid === false ? "email-error" : undefined}
                    />
                    {emailValid !== null && (
                      <div className="absolute right-4 top-1/2 -translate-y-1/2">
                        {emailValid ? (
                          <svg className="w-4 h-4 text-mint-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        ) : (
                          <svg className="w-4 h-4 text-peach-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        )}
                      </div>
                    )}
                  </div>
                  {emailValid === false && (
                    <p id="email-error" className="mt-1 text-peach-600 text-xs">
                      Please enter a valid email address
                    </p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={loading || !emailValid}
                  className="w-full px-6 py-4 bg-sage-100/80 hover:bg-sage-200/60 
                             text-sage-600 rounded-2xl text-base font-light border border-sage-200/50 hover:border-sage-300/60
                             transition-colors duration-300 focus:outline-none focus:ring-4 focus:ring-sage-100
                             disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-current" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Sending...
                    </span>
                  ) : (
                    'Send Reset Instructions'
                  )}
                </button>
              </form>
            )}

            <div className="text-center mt-8">
              <Link
                to="/login"
                className="text-sage-500 text-sm font-light hover:text-sage-600 transition-colors"
              >
                ← Back to login
              </Link>
            </div>
          </div>
        </div>
      </div>
    </NaturalBackground>
  )
}

export default ForgotPassword